import { RichText } from "./RichText";
import { Field, Select, TextArea, TextInput } from "./Field";
import { Panel, PanelHead } from "./Panel";
import { TYPE_LABEL, TYPE_OPTIONS } from "../data/status";
import type { RequestType } from "../data/types";

/** The required title field of a create dialog, with its "required" message
 *  shown only once the field has been touched. */
export function TitleField({
  id,
  value,
  onChange,
  touched,
  onBlur,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  touched: boolean;
  onBlur: () => void;
  placeholder: string;
}) {
  const missing = value.trim() === "";
  return (
    <Field label="Title" htmlFor={id}>
      <TextInput id={id} value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} aria-invalid={touched && missing} placeholder={placeholder} />
      {touched && missing && <span className="text-xs text-error-600">A title is required.</span>}
    </Field>
  );
}

/** The request-type select, same options in every create dialog. */
export function TypeField({ id, value, onChange }: { id: string; value: RequestType; onChange: (value: RequestType) => void }) {
  return (
    <Field label="Type" htmlFor={id}>
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value as RequestType)}>
        {TYPE_OPTIONS.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** The example that nudges a customer (or us) to describe the problem rather
 *  than prescribe a feature. Shown wherever the desired outcome is typed. */
export const OUTCOME_PLACEHOLDER = "We need our internal system to know when X happens in Euler so that we can trigger Y automatically.";

/** The "Desired outcome" panel both detail pages share: the problem, before
 *  any solution. Shown only once there is one — or while editing — so older
 *  requests do not carry an empty box. */
export function OutcomePanel({ id, value, editing, draft, onDraftChange }: { id: string; value: string; editing: boolean; draft: string; onDraftChange: (v: string) => void }) {
  if (!editing && value === "") return null;
  return (
    <Panel labelledBy={id}>
      <PanelHead id={id} title="Desired outcome" />
      {editing ? (
        <div className="px-4 py-4">
          <TextArea aria-label="Edit desired outcome" value={draft} onChange={(e) => onDraftChange(e.target.value)} placeholder={OUTCOME_PLACEHOLDER} />
        </div>
      ) : (
        <RichText text={value} />
      )}
    </Panel>
  );
}

/** The request heading both detail pages share: the title, or its edit box
 *  while editing, over the "id · account · type" line. */
export function RequestHeading({
  request,
  editing,
  title,
  onTitleChange,
}: {
  request: { id: string; title: string; account: string; type: RequestType };
  editing: boolean;
  title: string;
  onTitleChange: (v: string) => void;
}) {
  return (
    <div className="min-w-0 flex-1">
      {editing ? (
        <TextInput aria-label="Edit title" value={title} onChange={(e) => onTitleChange(e.target.value)} className="text-lg font-semibold" />
      ) : (
        <h1 className="text-display-xs font-semibold text-gray-900">{request.title}</h1>
      )}
      <p className="mt-1 text-sm text-[var(--text-2)]">
        {request.id} · {request.account} · {TYPE_LABEL[request.type]}
      </p>
    </div>
  );
}
